import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

type Turn = {
  id: string;
  session_id: string;
  org_id: string;
  user_input: string | null;
  assistant_output: string | null;
  task_class: string | null;
  session_kind: string | null;
  marked_as_golden_id: string | null;
  created_at: string;
};

type Persona = { id: string; name: string };

export function JarvisTrainingTab() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [defaultPersona, setDefaultPersona] = useState<string>("");
  const [perTurnPersona, setPerTurnPersona] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [turnsRes, personasRes] = await Promise.all([
        supabase.from("jarvis_session_turns")
          .select("id, session_id, org_id, user_input, assistant_output, task_class, session_kind, marked_as_golden_id, created_at")
          .not("user_input", "is", null)
          .order("created_at", { ascending: false })
          .limit(40),
        supabase.from("syra_ai_agents_safe")
          .select("id, name")
          .eq("jarvis_capability", true)
          .order("name"),
      ]);
      if (cancelled) return;
      if (turnsRes.error) toast.error(turnsRes.error.message);
      setTurns((turnsRes.data ?? []) as unknown as Turn[]);
      const ps = (personasRes.data ?? []) as Persona[];
      setPersonas(ps);
      if (ps[0]) setDefaultPersona(ps[0].id);
      setLoading(false);
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  const promoteToGolden = async (t: Turn) => {
    const personaId = perTurnPersona[t.id] || defaultPersona;
    if (!personaId || !t.user_input) {
      toast.error("Selecione uma persona e turno com texto."); return;
    }
    setSavingId(t.id);
    const inputMode = (t.session_kind && ["event", "training", "conversation"].includes(t.session_kind))
      ? t.session_kind : "conversation";
    const { data, error } = await supabase.from("jarvis_persona_golden").insert({
      persona_id: personaId,
      org_id: t.org_id,
      input_text: t.user_input,
      input_mode: inputMode,
      tag: "tone",
      expected_substrings: t.assistant_output ? [t.assistant_output.slice(0, 60)] : [],
    }).select("id").single();
    if (!error && data?.id) {
      await supabase.from("jarvis_session_turns").update({ marked_as_golden_id: data.id }).eq("id", t.id);
    }
    setSavingId(null);
    if (error) { toast.error(error.message); return; }
    toast.success("Adicionado ao golden set");
    setTurns(prev => prev.map(x => x.id === t.id ? { ...x, marked_as_golden_id: data?.id ?? null } : x));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold font-headline">Treino — Golden Set</h2>
          <p className="text-xs text-muted-foreground">Marque turnos exemplares como casos golden para validar publicação.</p>
        </div>
        {personas.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Persona padrão:</span>
            <Select value={defaultPersona} onValueChange={setDefaultPersona}>
              <SelectTrigger className="h-8 w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {personas.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {turns.length === 0 && (
        <Card className="p-8 text-center text-sm text-muted-foreground">Sem turnos recentes.</Card>
      )}
      <div className="space-y-2">
        {turns.map((t) => {
          const isGolden = !!t.marked_as_golden_id;
          return (
            <Card key={t.id} className="p-3 space-y-2">
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                <Badge variant="outline" className="text-[10px]">{t.session_kind ?? "—"}</Badge>
                <Badge variant="outline" className="text-[10px]">{t.task_class ?? "—"}</Badge>
                {isGolden && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">golden</Badge>}
                <span className="ml-auto tabular-nums">{new Date(t.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm">
                <div className="text-muted-foreground text-xs">Usuário</div>
                <div className="line-clamp-2">{t.user_input}</div>
              </div>
              <div className="text-sm">
                <div className="text-muted-foreground text-xs">Jarvis</div>
                <div className="line-clamp-3">{t.assistant_output || <span className="italic text-muted-foreground">— vazio —</span>}</div>
              </div>
              <div className="flex justify-end items-center gap-2">
                <Select value={perTurnPersona[t.id] ?? defaultPersona} onValueChange={(v) => setPerTurnPersona(prev => ({ ...prev, [t.id]: v }))}>
                  <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder="Persona" /></SelectTrigger>
                  <SelectContent>
                    {personas.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={savingId === t.id || isGolden} onClick={() => promoteToGolden(t)}>
                  {savingId === t.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Star className="w-3 h-3 mr-1" />}
                  {isGolden ? "Já é golden" : "Marcar golden"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
      <Card className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        Persona só pode ser publicada com ≥10 golden cases e pass rate ≥85%.
      </Card>
    </div>
  );
}
